// src/features/product/components/AddProductModal.jsx
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Container,
  InputGroup,
} from "react-bootstrap";
import { useCategories } from "../../categories/hooks/useCategories";
import { useAgeGroupProducts } from "../../age-group-product/hooks/useAgeGroupProducts";
import useTypeProduct from "../../type-product/hooks/useTypeProduct";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { usePromoProduct } from "../../promo/hooks/usePromoProduct";
import { useProducts } from "../hooks/useProducts";
import WindowedSelect from "react-windowed-select";

const ProductModalAdd = ({
  editProduct,
  editingData,
  show,
  handleClose,
  handleAddProduct,
  parsingDataEdit,
}) => {
  const { categories } = useCategories();
  const { ageGroupProduct } = useAgeGroupProducts();
  const { typeProduct } = useTypeProduct();
  const { masterproducts, fetchMasterProducts } = useProducts();
  const { promoProduct } = usePromoProduct();

  // Quill editors
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const [editorValue, setEditorValue] = useState("");
  const editorTermAndConditionRef = useRef(null);
  const quillTermAndConditionRef = useRef(null);
  const [editorTermAndConditionValue, setEditorTermAndConditionValue] = useState("");

  // State untuk upload gambar
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageSelection, setImageSelection] = useState("existing"); // "existing" | "upload"

  // State product
  const [product, setProduct] = useState({
    name: "",
    release_date: "",
    expired_date: "",
    is_new_release: 0,
    is_promotion_hospital: 0,
    is_promotion_package: 0,
    is_same_price: 0,
    is_featured: 0,
    image: "",
    id_paket: "",
    category: "",
    typeProduct: "",
    ageGroupProduct: "",
    alias_name: "",
    slug: "",
    sex: "",
    meta_title: "",
    starting_price: 0,
    promo_price: 0,
    is_insurance: 0,
    is_price_hide: 0,
    promo_code: "",
    promo_name: "",
    categoryString: "",
    typeProductString: "",
    ageGroupProductString: "",
  });

  // State untuk pilihan gambar (dari package)
  const [newImageUrl, setNewImageUrl] = useState("");

  // Fetch master products once
  useEffect(() => {
    fetchMasterProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track previous editingData to avoid infinite loop
  const prevEditingDataRef = useRef();

  // === RESET FUNCTION ===
  const resetModal = useCallback(() => {
    setProduct({
      name: "",
      release_date: "",
      expired_date: "",
      is_new_release: 0,
      is_promotion_hospital: 0,
      is_promotion_package: 0,
      is_same_price: 0,
      is_featured: 0,
      image: "",
      id_paket: "",
      category: "",
      typeProduct: "",
      ageGroupProduct: "",
      alias_name: "",
      slug: "",
      sex: "",
      meta_title: "",
      starting_price: 0,
      promo_price: 0,
      is_insurance: 0,
      is_price_hide: 0,
      promo_code: "",
      promo_name: "",
      categoryString: "",
      typeProductString: "",
      ageGroupProductString: "",
    });
    setNewImageUrl("");
    setSelectedFile(null);
    setPreviewUrl("");
    setImageSelection("existing");
    setEditorValue("");
    setEditorTermAndConditionValue("");
    if (quillRef.current) {
      quillRef.current.root.innerHTML = "";
    }
    if (quillTermAndConditionRef.current) {
      quillTermAndConditionRef.current.root.innerHTML = "";
    }
    prevEditingDataRef.current = null;
  }, []);

  // Update product when editingData changes
  useEffect(() => {
    if (show && editingData && prevEditingDataRef.current !== editingData) {
      const categoryId = editingData.category ? String(editingData.category) : "";
      const ageGroupId = editingData.ageGroupProduct ? String(editingData.ageGroupProduct) : "";
      const typeId = editingData.typeProduct ? String(editingData.typeProduct) : "";

      setProduct({
        name: editingData.name || "",
        release_date: editingData.release_date || "",
        expired_date: editingData.expired_date || "",
        is_new_release: editingData.is_new_release || 0,
        is_promotion_hospital: editingData.is_promotion_hospital || 0,
        is_promotion_package: editingData.is_promotion_package || 0,
        is_same_price: editingData.is_same_price || 0,
        is_featured: editingData.is_featured || 0,
        image: editingData.image || "",
        id_paket: editingData.id_paket || "",
        category: categoryId,
        typeProduct: typeId,
        ageGroupProduct: ageGroupId,
        alias_name: editingData.alias_name || "",
        slug: editingData.slug || "",
        sex: editingData.sex || "",
        meta_title: editingData.meta_title || "",
        starting_price: parseInt(editingData.starting_price, 10) || 0,
        promo_price: parseInt(editingData.promo_price, 10) || 0,
        is_insurance: editingData.is_insurance || 0,
        is_price_hide: editingData.is_price_hide || 0,
        promo_code: editingData.promo_code || "",
        promo_name: editingData.promo_name || "",
        categoryString: editingData.categoryString || "",
        typeProductString: editingData.typeProductString || "",
        ageGroupProductString: editingData.ageGroupProductString || "",
      });
      // Set image selection
      if (editingData.image) {
        setImageSelection("existing");
      } else {
        setImageSelection("upload");
      }
      setNewImageUrl("");
      setSelectedFile(null);
      setPreviewUrl("");
      // Set Quill content
      if (quillRef.current && editingData.description) {
        quillRef.current.root.innerHTML = editingData.description || "";
        setEditorValue(editingData.description || "");
      }
      if (quillTermAndConditionRef.current && editingData.term_condition) {
        quillTermAndConditionRef.current.root.innerHTML = editingData.term_condition || "";
        setEditorTermAndConditionValue(editingData.term_condition || "");
      }
      prevEditingDataRef.current = editingData;
    }

    if (!show) {
      resetModal();
    }
  }, [show, editingData, resetModal]);

  // Sinkronisasi ID dari string jika data master sudah tersedia
  useEffect(() => {
    if (!show || !editingData) return;

    if (!product.category && product.categoryString && categories.length > 0) {
      const found = categories.find(cat => cat.name === product.categoryString);
      if (found) {
        setProduct(prev => ({ ...prev, category: String(found.id) }));
      }
    }

    if (!product.ageGroupProduct && product.ageGroupProductString && ageGroupProduct.length > 0) {
      const found = ageGroupProduct.find(age => age.name === product.ageGroupProductString);
      if (found) {
        setProduct(prev => ({ ...prev, ageGroupProduct: String(found.id) }));
      }
    }

    if (!product.typeProduct && product.typeProductString && typeProduct.length > 0) {
      const found = typeProduct.find(type => type.name === product.typeProductString);
      if (found) {
        setProduct(prev => ({ ...prev, typeProduct: String(found.id) }));
      }
    }
  }, [show, editingData, categories, ageGroupProduct, typeProduct, product.category, product.ageGroupProduct, product.typeProduct, product.categoryString, product.ageGroupProductString, product.typeProductString]);

  // Initialize Quill editors
  useEffect(() => {
    if (!show) return;
    const existingToolbars = document.querySelectorAll(".ql-toolbar");
    existingToolbars.forEach((toolbar) => toolbar.remove());

    if (editorRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: "1" }, { header: "2" }, { font: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["bold", "italic", "underline"],
            [{ align: [] }],
            ["link"],
            ["image"],
          ],
        },
      });
      quillRef.current.on("text-change", () => {
        setEditorValue(quillRef.current.root.innerHTML);
      });
    }

    if (editorTermAndConditionRef.current && !quillTermAndConditionRef.current) {
      quillTermAndConditionRef.current = new Quill(editorTermAndConditionRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: "1" }, { header: "2" }, { font: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["bold", "italic", "underline"],
            [{ align: [] }],
            ["link"],
            ["image"],
          ],
        },
      });
      quillTermAndConditionRef.current.on("text-change", () => {
        setEditorTermAndConditionValue(quillTermAndConditionRef.current.root.innerHTML);
      });
    }

    return () => {
      quillRef.current = null;
      quillTermAndConditionRef.current = null;
    };
  }, [show]);

  // Prepare options for WindowedSelect
  const productOptions = useMemo(() => {
    return masterproducts.map((p) => ({
      value: p.id_paket,
      label: p.nama_paket,
      imageurl: p.imageurl,
      nilai_paket: p.nilai_paket,
    }));
  }, [masterproducts]);

  // Handle product selection from dropdown
  const handleProductSelect = useCallback((selectedOption) => {
    if (!selectedOption) {
      setProduct((prev) => ({
        ...prev,
        name: "",
        image: "",
        id_paket: "",
        alias_name: "",
        meta_title: "",
        starting_price: 0,
      }));
      setNewImageUrl("");
      setSelectedFile(null);
      setPreviewUrl("");
      setImageSelection("upload");
      return;
    }
    const newImg = selectedOption.imageurl || "";
    setNewImageUrl(newImg);
    if (newImg) {
      setProduct((prev) => ({
        ...prev,
        name: selectedOption.label,
        id_paket: selectedOption.value,
        alias_name: selectedOption.label,
        meta_title: selectedOption.label,
        starting_price: parseInt(selectedOption.nilai_paket, 10) || 0,
        image: newImg,
      }));
      setImageSelection("existing");
    } else {
      setProduct((prev) => ({
        ...prev,
        name: selectedOption.label,
        id_paket: selectedOption.value,
        alias_name: selectedOption.label,
        meta_title: selectedOption.label,
        starting_price: parseInt(selectedOption.nilai_paket, 10) || 0,
        image: "",
      }));
      setImageSelection("upload");
    }
    // Reset file upload
    setSelectedFile(null);
    setPreviewUrl("");
  }, []);

  // Handler untuk radio pilihan gambar
  const handleImageOptionChange = (e) => {
    const value = e.target.value;
    setImageSelection(value);
    if (value === "existing") {
      // Gunakan gambar yang sudah ada (dari editing atau package)
      const existingImage = editingData?.image || newImageUrl || "";
      setProduct((prev) => ({ ...prev, image: existingImage }));
      setSelectedFile(null);
      setPreviewUrl("");
    } else {
      // Upload baru: kosongkan image, akan diisi saat file dipilih
      setProduct((prev) => ({ ...prev, image: "" }));
      setSelectedFile(null);
      setPreviewUrl("");
    }
  };

  // Handler untuk upload file
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setPreviewUrl(base64);
        setProduct((prev) => ({ ...prev, image: base64 }));
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setPreviewUrl("");
      setProduct((prev) => ({ ...prev, image: "" }));
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "promo_code") {
      const selectedPromo = promoProduct.find((promo) => promo.code_promo === value);
      const discount = selectedPromo ? parseFloat(selectedPromo.discount) : 0;
      const price = parseInt(product.starting_price, 10) || 0;
      const discountedPrice = price > 0 ? price - (price * discount) / 100 : 0;
      setProduct((prev) => ({
        ...prev,
        promo_code: value,
        promo_name: selectedPromo?.name || "",
        promo_price: Math.round(discountedPrice),
      }));
    } else if (name === "category") {
      const selectedCategory = categories.find((cat) => String(cat.id) === value);
      setProduct((prev) => ({
        ...prev,
        [name]: value,
        categoryString: selectedCategory?.name || "",
      }));
    } else if (name === "ageGroupProduct") {
      const selectedAgeGroup = ageGroupProduct.find((age) => String(age.id) === value);
      setProduct((prev) => ({
        ...prev,
        [name]: value,
        ageGroupProductString: selectedAgeGroup?.name || "",
      }));
    } else if (name === "typeProduct") {
      const selectedType = typeProduct.find((type) => String(type.id) === value);
      setProduct((prev) => ({
        ...prev,
        [name]: value,
        typeProductString: selectedType?.name || "",
      }));
    } else if (type === "checkbox") {
      setProduct((prev) => ({
        ...prev,
        [name]: checked ? 1 : 0,
      }));
    } else {
      let newValue = value;
      if (name === "starting_price") {
        newValue = parseInt(value, 10) || 0;
      }
      setProduct((prev) => {
        const updated = { ...prev, [name]: newValue };
        if (name === "starting_price" && prev.promo_code) {
          const selected = promoProduct.find((p) => p.code_promo === prev.promo_code);
          if (selected) {
            const discount = parseFloat(selected.discount);
            const price = parseInt(value, 10) || 0;
            updated.promo_price = Math.round(price - (price * discount) / 100);
          }
        }
        return updated;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingData?.id != null) {
      await editProduct(editingData.id, { ...parsingDataEdit, ...product });
    } else {
      await handleAddProduct(product);
    }
    handleClose();
  };

  const showSelect = product.is_promotion_hospital === 1 || product.is_promotion_package === 1;

  // Tentukan apakah ada gambar existing (dari editing atau package)
  const existingImage = editingData?.image || newImageUrl || "";

  return (
    <Modal
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={show}
      onHide={handleClose}
      dialogClassName="fullscreen-modal"
      style={{
        maxWidth: "100%",
        height: "100vh",
        margin: 0,
        padding: 0,
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 13 }}>Add New Product</Modal.Title>
      </Modal.Header>
      <Modal.Body className="grid-example">
        <Form onSubmit={handleSubmit}>
          <Container>
            <Row>
              <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                <Form.Label>Select Product</Form.Label>
                <WindowedSelect
                  options={productOptions}
                  value={productOptions.find((opt) => opt.value === product.id_paket) || null}
                  onChange={handleProductSelect}
                  placeholder="-- Select or type a product --"
                  isClearable
                  isSearchable
                  styles={{
                    control: (base) => ({
                      ...base,
                      fontSize: 13,
                      minHeight: "38px",
                    }),
                    placeholder: (base) => ({ ...base, fontSize: 13 }),
                    singleValue: (base) => ({ ...base, fontSize: 13 }),
                    option: (base) => ({ ...base, fontSize: 13 }),
                  }}
                  windowThreshold={20}
                  optionHeight={35}
                  maxHeight={200}
                />
              </Form.Group>
            </Row>
            <Row>
              <Col xs={6} md={6}>
                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Category {editingData?.categoryString}</Form.Label>
                  <Form.Select
                    name="category"
                    value={product.category || ""}
                    onChange={handleChange}
                    style={{ fontSize: 13 }}
                    className={editingData?.categoryString ? "" : "is-invalid"}
                    required={!editingData?.categoryString}
                  >
                    <option value="" disabled>-- Select a category --</option>
                    {categories.map((category) => (
                      <option style={{ fontSize: 13 }} key={category.id} value={String(category.id)}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                  {!product.category && (
                    <div className="invalid-feedback">Category is required.</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Age Group Product {editingData?.ageGroupProductString}</Form.Label>
                  <Form.Select
                    name="ageGroupProduct"
                    value={product.ageGroupProduct || ""}
                    onChange={handleChange}
                    style={{ fontSize: 13 }}
                    className={editingData?.ageGroupProductString ? "" : "is-invalid"}
                    required={!editingData?.ageGroupProductString}
                  >
                    <option value="" disabled>-- Select a Age Group --</option>
                    {ageGroupProduct.map((ageGroup) => (
                      <option style={{ fontSize: 13 }} key={ageGroup.id} value={String(ageGroup.id)}>
                        {ageGroup.name}
                      </option>
                    ))}
                  </Form.Select>
                  {!product.ageGroupProduct && (
                    <div className="invalid-feedback">Age Group is required.</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Type Product {editingData?.typeProductString}</Form.Label>
                  <Form.Select
                    name="typeProduct"
                    value={product.typeProduct || ""}
                    onChange={handleChange}
                    style={{ fontSize: 13 }}
                    className={editingData?.typeProductString ? "" : "is-invalid"}
                    required={!editingData?.typeProductString}
                  >
                    <option value="" disabled>-- Select a Type --</option>
                    {typeProduct.map((type) => (
                      <option style={{ fontSize: 13 }} key={type.id} value={String(type.id)}>
                        {type.name}
                      </option>
                    ))}
                  </Form.Select>
                  {!product.typeProduct && (
                    <div className="invalid-feedback">Type is required.</div>
                  )}
                </Form.Group>

                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Price</Form.Label>
                  <Form.Control
                    type="number"
                    name="starting_price"
                    value={product.starting_price ?? ""}
                    onChange={handleChange}
                    style={{ fontSize: 13 }}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Release and Expired</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="date"
                      name="release_date"
                      value={product.release_date || ""}
                      onChange={handleChange}
                      placeholder="Release date"
                      style={{ fontSize: 13 }}
                    />
                    <Form.Control
                      type="date"
                      name="expired_date"
                      value={product.expired_date || ""}
                      onChange={handleChange}
                      placeholder="Expired date"
                      style={{ fontSize: 13 }}
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Sex Category</Form.Label>
                  <Form.Select
                    name="sex"
                    value={product.sex || ""}
                    onChange={handleChange}
                    style={{ fontSize: 13 }}
                    required
                  >
                    <option value="" disabled>-- Select a Sex Category --</option>
                    <option value="Laki-laki">Laki - laki</option>
                    <option value="Perempuan">Perempuan</option>
                    <option value="Umum">Umum</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={6} md={6}>
                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Name and Id Paket</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      name="name"
                      value={product.name || ""}
                      onChange={handleChange}
                      placeholder="Name Product"
                      required
                    />
                    <Form.Control
                      type="text"
                      name="id_paket"
                      value={product.id_paket || ""}
                      onChange={handleChange}
                      placeholder="Id Paket"
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                  <Form.Label>Alias Name and Meta Title</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      name="alias_name"
                      value={product.alias_name || ""}
                      onChange={handleChange}
                      placeholder="Alias Name"
                      required
                    />
                    <Form.Control
                      type="text"
                      name="meta_title"
                      value={product.meta_title || ""}
                      onChange={handleChange}
                      placeholder="Meta Title"
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Row>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="is_featured"
                        name="is_featured"
                        label="Featured"
                        checked={product.is_featured === 1}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="is_insurance"
                        name="is_insurance"
                        label="Insurance"
                        checked={product.is_insurance === 1}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="is_new_release"
                        name="is_new_release"
                        label="New release"
                        checked={product.is_new_release === 1}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="is_same_price"
                        name="is_same_price"
                        label="Is same price"
                        checked={product.is_same_price === 1}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="is_price_hide"
                        name="is_price_hide"
                        label="Price hide"
                        checked={product.is_price_hide === 1}
                        onChange={handleChange}
                      />
                    </Col>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="is_promotion_package"
                        name="is_promotion_package"
                        label="Promotion Package"
                        checked={product.is_promotion_package === 1}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {showSelect && (
                        <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                          <Form.Label>Select Promotion</Form.Label>
                          <Form.Select
                            name="promo_code"
                            value={product.promo_code || ""}
                            onChange={handleChange}
                            style={{ fontSize: 13 }}
                          >
                            <option value="" disabled>-- Select a Promotion --</option>
                            {promoProduct.map((promo) => (
                              <option
                                style={{ fontSize: 13 }}
                                key={promo.id}
                                value={promo.code_promo}
                                data-discount={promo.discount}
                              >
                                {promo.name} - Discount {promo.discount} %
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}
                      {product.promo_price ? (
                        <Form.Group className="mb-2" style={{ fontSize: 13 }}>
                          <Form.Label>Promo Price</Form.Label>
                          <Form.Control
                            type="number"
                            name="promo_price"
                            value={product.promo_price ?? ""}
                            readOnly
                            style={{ fontSize: 13 }}
                          />
                        </Form.Group>
                      ) : null}
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
            </Row>

            {/* Bagian Pemilihan Gambar */}
            <Row>
              <Col xs={12} md={12}>
                <div className="mt-3">
                  <h5>Image</h5>
                  {existingImage ? (
                    <>
                      <Form.Group>
                        <Form.Check
                          type="radio"
                          label="Use existing image"
                          name="imageOption"
                          value="existing"
                          checked={imageSelection === "existing"}
                          onChange={handleImageOptionChange}
                        />
                        <Form.Check
                          type="radio"
                          label="Upload new image"
                          name="imageOption"
                          value="upload"
                          checked={imageSelection === "upload"}
                          onChange={handleImageOptionChange}
                        />
                      </Form.Group>
                      {imageSelection === "existing" && (
                        <div>
                          <img
                            src={existingImage}
                            alt="Existing"
                            style={{ maxWidth: "200px", maxHeight: "150px", border: "1px solid #ccc" }}
                          />
                        </div>
                      )}
                      {imageSelection === "upload" && (
                        <div>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                          {previewUrl && (
                            <img
                              src={previewUrl}
                              alt="Preview"
                              style={{ maxWidth: "200px", maxHeight: "150px", marginTop: "10px", border: "1px solid #ccc" }}
                            />
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    // Tidak ada gambar existing, langsung tampilkan upload
                    <div>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      {previewUrl && (
                        <img
                          src={previewUrl}
                          alt="Preview"
                          style={{ maxWidth: "200px", maxHeight: "150px", marginTop: "10px", border: "1px solid #ccc" }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <Button style={{ fontSize: 13 }} variant="primary" type="submit">
              {editingData?.id ? "Update Product" : "Add Product"}
            </Button>
          </Container>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ProductModalAdd;

// // src/features/product/components/AddProductModal.jsx
// import { useEffect, useRef, useState, useMemo, useCallback } from "react";
// import {
//   Modal,
//   Button,
//   Form,
//   Row,
//   Col,
//   Container,
//   InputGroup,
// } from "react-bootstrap";
// import { useCategories } from "../../categories/hooks/useCategories";
// import { useAgeGroupProducts } from "../../age-group-product/hooks/useAgeGroupProducts";
// import useTypeProduct from "../../type-product/hooks/useTypeProduct";
// import Quill from "quill";
// import "quill/dist/quill.snow.css";
// import { usePromoProduct } from "../../promo/hooks/usePromoProduct";
// import { useProducts } from "../hooks/useProducts";
// import WindowedSelect from "react-windowed-select";

// const ProductModalAdd = ({
//   editProduct,
//   editingData,
//   show,
//   handleClose,
//   handleAddProduct,
//   parsingDataEdit,
// }) => {
//   const { categories } = useCategories();
//   const { ageGroupProduct } = useAgeGroupProducts();
//   const { typeProduct } = useTypeProduct();
//   const { masterproducts, fetchMasterProducts } = useProducts();
//   const { promoProduct } = usePromoProduct();

//   // Quill editors
//   const editorRef = useRef(null);
//   const quillRef = useRef(null);
//   const [editorValue, setEditorValue] = useState("");
//   const editorTermAndConditionRef = useRef(null);
//   const quillTermAndConditionRef = useRef(null);
//   const [editorTermAndConditionValue, setEditorTermAndConditionValue] = useState("");

//   const [preview, setPreview] = useState(null);

//   // State product
//   const [product, setProduct] = useState({
//     name: "",
//     release_date: "",
//     expired_date: "",
//     is_new_release: 0,
//     is_promotion_hospital: 0,
//     is_promotion_package: 0,
//     is_same_price: 0,
//     is_featured: 0,
//     image: "",
//     id_paket: "",
//     category: "",
//     typeProduct: "",
//     ageGroupProduct: "",
//     alias_name: "",
//     slug: "",
//     sex: "",
//     meta_title: "",
//     starting_price: 0,
//     promo_price: 0,
//     is_insurance: 0,
//     is_price_hide: 0,
//     promo_code: "",
//     promo_name: "",
//     categoryString: "",
//     typeProductString: "",
//     ageGroupProductString: "",
//   });

//   // State untuk pilihan gambar
//   const [imageChoice, setImageChoice] = useState("old"); // "old" atau "new"
//   const [newImageUrl, setNewImageUrl] = useState("");

//   // Fetch master products once
//   useEffect(() => {
//     fetchMasterProducts();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // Track previous editingData to avoid infinite loop
//   const prevEditingDataRef = useRef();

//   // === RESET FUNCTION ===
//   const resetModal = useCallback(() => {
//     // Reset product state ke default
//     setProduct({
//       name: "",
//       release_date: "",
//       expired_date: "",
//       is_new_release: 0,
//       is_promotion_hospital: 0,
//       is_promotion_package: 0,
//       is_same_price: 0,
//       is_featured: 0,
//       image: "",
//       id_paket: "",
//       category: "",
//       typeProduct: "",
//       ageGroupProduct: "",
//       alias_name: "",
//       slug: "",
//       sex: "",
//       meta_title: "",
//       starting_price: 0,
//       promo_price: 0,
//       is_insurance: 0,
//       is_price_hide: 0,
//       promo_code: "",
//       promo_name: "",
//       categoryString: "",
//       typeProductString: "",
//       ageGroupProductString: "",
//     });
//     setPreview(null);
//     setImageChoice("old");
//     setNewImageUrl("");
//     setEditorValue("");
//     setEditorTermAndConditionValue("");
//     // Kosongkan Quill editor jika ada
//     if (quillRef.current) {
//       quillRef.current.root.innerHTML = "";
//     }
//     if (quillTermAndConditionRef.current) {
//       quillTermAndConditionRef.current.root.innerHTML = "";
//     }
//     prevEditingDataRef.current = null;
//   }, []);

//   // Update product when editingData changes (only if modal is open)
//   useEffect(() => {
//     if (show && editingData && prevEditingDataRef.current !== editingData) {
//       // Ubah ID menjadi string agar cocok dengan value dropdown
//       const categoryId = editingData.category ? String(editingData.category) : "";
//       const ageGroupId = editingData.ageGroupProduct ? String(editingData.ageGroupProduct) : "";
//       const typeId = editingData.typeProduct ? String(editingData.typeProduct) : "";

//       setProduct({
//         name: editingData.name || "",
//         release_date: editingData.release_date || "",
//         expired_date: editingData.expired_date || "",
//         is_new_release: editingData.is_new_release || 0,
//         is_promotion_hospital: editingData.is_promotion_hospital || 0,
//         is_promotion_package: editingData.is_promotion_package || 0,
//         is_same_price: editingData.is_same_price || 0,
//         is_featured: editingData.is_featured || 0,
//         image: editingData.image || "",
//         id_paket: editingData.id_paket || "",
//         category: categoryId,
//         typeProduct: typeId,
//         ageGroupProduct: ageGroupId,
//         alias_name: editingData.alias_name || "",
//         slug: editingData.slug || "",
//         sex: editingData.sex || "",
//         meta_title: editingData.meta_title || "",
//         starting_price: parseInt(editingData.starting_price, 10) || 0,
//         promo_price: parseInt(editingData.promo_price, 10) || 0,
//         is_insurance: editingData.is_insurance || 0,
//         is_price_hide: editingData.is_price_hide || 0,
//         promo_code: editingData.promo_code || "",
//         promo_name: editingData.promo_name || "",
//         categoryString: editingData.categoryString || "",
//         typeProductString: editingData.typeProductString || "",
//         ageGroupProductString: editingData.ageGroupProductString || "",
//       });
//       setImageChoice(editingData.image ? "old" : "new");
//       setNewImageUrl("");
//       // Set konten Quill jika ada
//       if (quillRef.current && editingData.description) {
//         quillRef.current.root.innerHTML = editingData.description || "";
//         setEditorValue(editingData.description || "");
//       }
//       if (quillTermAndConditionRef.current && editingData.term_condition) {
//         quillTermAndConditionRef.current.root.innerHTML = editingData.term_condition || "";
//         setEditorTermAndConditionValue(editingData.term_condition || "");
//       }
//       prevEditingDataRef.current = editingData;
//     }

//     // Reset form when modal closes without editing
//     if (!show) {
//       resetModal();
//     }
//   }, [show, editingData, resetModal]);

//   // Sinkronisasi ID dari string jika data master sudah tersedia
//   useEffect(() => {
//     if (!show || !editingData) return;

//     if (!product.category && product.categoryString && categories.length > 0) {
//       const found = categories.find(cat => cat.name === product.categoryString);
//       if (found) {
//         setProduct(prev => ({ ...prev, category: String(found.id) }));
//       }
//     }

//     if (!product.ageGroupProduct && product.ageGroupProductString && ageGroupProduct.length > 0) {
//       const found = ageGroupProduct.find(age => age.name === product.ageGroupProductString);
//       if (found) {
//         setProduct(prev => ({ ...prev, ageGroupProduct: String(found.id) }));
//       }
//     }

//     if (!product.typeProduct && product.typeProductString && typeProduct.length > 0) {
//       const found = typeProduct.find(type => type.name === product.typeProductString);
//       if (found) {
//         setProduct(prev => ({ ...prev, typeProduct: String(found.id) }));
//       }
//     }
//   }, [show, editingData, categories, ageGroupProduct, typeProduct, product.category, product.ageGroupProduct, product.typeProduct, product.categoryString, product.ageGroupProductString, product.typeProductString]);

//   // Initialize Quill editors
//   useEffect(() => {
//     if (!show) return;
//     const existingToolbars = document.querySelectorAll(".ql-toolbar");
//     existingToolbars.forEach((toolbar) => toolbar.remove());

//     if (editorRef.current && !quillRef.current) {
//       quillRef.current = new Quill(editorRef.current, {
//         theme: "snow",
//         modules: {
//           toolbar: [
//             [{ header: "1" }, { header: "2" }, { font: [] }],
//             [{ list: "ordered" }, { list: "bullet" }],
//             ["bold", "italic", "underline"],
//             [{ align: [] }],
//             ["link"],
//             ["image"],
//           ],
//         },
//       });
//       quillRef.current.on("text-change", () => {
//         setEditorValue(quillRef.current.root.innerHTML);
//       });
//     }

//     if (editorTermAndConditionRef.current && !quillTermAndConditionRef.current) {
//       quillTermAndConditionRef.current = new Quill(editorTermAndConditionRef.current, {
//         theme: "snow",
//         modules: {
//           toolbar: [
//             [{ header: "1" }, { header: "2" }, { font: [] }],
//             [{ list: "ordered" }, { list: "bullet" }],
//             ["bold", "italic", "underline"],
//             [{ align: [] }],
//             ["link"],
//             ["image"],
//           ],
//         },
//       });
//       quillTermAndConditionRef.current.on("text-change", () => {
//         setEditorTermAndConditionValue(quillTermAndConditionRef.current.root.innerHTML);
//       });
//     }

//     return () => {
//       quillRef.current = null;
//       quillTermAndConditionRef.current = null;
//     };
//   }, [show]);

//   // Prepare options for WindowedSelect
//   const productOptions = useMemo(() => {
//     return masterproducts.map((p) => ({
//       value: p.id_paket,
//       label: p.nama_paket,
//       imageurl: p.imageurl,
//       nilai_paket: p.nilai_paket,
//     }));
//   }, [masterproducts]);

//   // Handle product selection
//   const handleProductSelect = useCallback((selectedOption) => {
//     if (!selectedOption) {
//       setProduct((prev) => ({
//         ...prev,
//         name: "",
//         image: "",
//         id_paket: "",
//         alias_name: "",
//         meta_title: "",
//         starting_price: 0,
//       }));
//       setNewImageUrl("");
//       setImageChoice("old");
//       return;
//     }
//     const newImg = selectedOption.imageurl || "";
//     setNewImageUrl(newImg);
//     setImageChoice("new");
//     setProduct((prev) => ({
//       ...prev,
//       name: selectedOption.label,
//       id_paket: selectedOption.value,
//       alias_name: selectedOption.label,
//       meta_title: selectedOption.label,
//       starting_price: parseInt(selectedOption.nilai_paket, 10) || 0,
//       image: newImg,
//     }));
//   }, []);

//   // Handler untuk memilih gambar (radio)
//   const handleImageChoiceChange = (choice) => {
//     setImageChoice(choice);
//     if (choice === "old") {
//       setProduct((prev) => ({ ...prev, image: editingData?.image || "" }));
//     } else {
//       setProduct((prev) => ({ ...prev, image: newImageUrl || "" }));
//     }
//   };

//   // Handle form field changes
//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;

//     if (name === "promo_code") {
//       const selectedPromo = promoProduct.find((promo) => promo.code_promo === value);
//       const discount = selectedPromo ? parseFloat(selectedPromo.discount) : 0;
//       const price = parseInt(product.starting_price, 10) || 0;
//       const discountedPrice = price > 0 ? price - (price * discount) / 100 : 0;
//       setProduct((prev) => ({
//         ...prev,
//         promo_code: value,
//         promo_name: selectedPromo?.name || "",
//         promo_price: Math.round(discountedPrice),
//       }));
//     } else if (name === "category") {
//       const selectedCategory = categories.find((cat) => String(cat.id) === value);
//       setProduct((prev) => ({
//         ...prev,
//         [name]: value,
//         categoryString: selectedCategory?.name || "",
//       }));
//     } else if (name === "ageGroupProduct") {
//       const selectedAgeGroup = ageGroupProduct.find((age) => String(age.id) === value);
//       setProduct((prev) => ({
//         ...prev,
//         [name]: value,
//         ageGroupProductString: selectedAgeGroup?.name || "",
//       }));
//     } else if (name === "typeProduct") {
//       const selectedType = typeProduct.find((type) => String(type.id) === value);
//       setProduct((prev) => ({
//         ...prev,
//         [name]: value,
//         typeProductString: selectedType?.name || "",
//       }));
//     } else if (type === "checkbox") {
//       setProduct((prev) => ({
//         ...prev,
//         [name]: checked ? 1 : 0,
//       }));
//     } else {
//       let newValue = value;
//       if (name === "starting_price") {
//         newValue = parseInt(value, 10) || 0;
//       }
//       setProduct((prev) => {
//         const updated = { ...prev, [name]: newValue };
//         if (name === "starting_price" && prev.promo_code) {
//           const selected = promoProduct.find((p) => p.code_promo === prev.promo_code);
//           if (selected) {
//             const discount = parseFloat(selected.discount);
//             const price = parseInt(value, 10) || 0;
//             updated.promo_price = Math.round(price - (price * discount) / 100);
//           }
//         }
//         return updated;
//       });
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (editingData?.id != null) {
//       await editProduct(editingData.id, { ...parsingDataEdit, ...product });
//     } else {
//       await handleAddProduct(product);
//     }
//     handleClose();
//   };

//   const showSelect = product.is_promotion_hospital === 1 || product.is_promotion_package === 1;

//   return (
//     <Modal
//       size="xl"
//       aria-labelledby="contained-modal-title-vcenter"
//       centered
//       show={show}
//       onHide={handleClose}
//       dialogClassName="fullscreen-modal"
//       style={{
//         maxWidth: "100%",
//         height: "100vh",
//         margin: 0,
//         padding: 0,
//       }}
//     >
//       <Modal.Header closeButton>
//         <Modal.Title style={{ fontSize: 13 }}>Add New Product</Modal.Title>
//       </Modal.Header>
//       <Modal.Body className="grid-example">
//         <Form onSubmit={handleSubmit}>
//           <Container>
//             <Row>
//               <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                 <Form.Label>Select Product</Form.Label>
//                 <WindowedSelect
//                   options={productOptions}
//                   value={productOptions.find((opt) => opt.value === product.id_paket) || null}
//                   onChange={handleProductSelect}
//                   placeholder="-- Select or type a product --"
//                   isClearable
//                   isSearchable
//                   styles={{
//                     control: (base) => ({
//                       ...base,
//                       fontSize: 13,
//                       minHeight: "38px",
//                     }),
//                     placeholder: (base) => ({ ...base, fontSize: 13 }),
//                     singleValue: (base) => ({ ...base, fontSize: 13 }),
//                     option: (base) => ({ ...base, fontSize: 13 }),
//                   }}
//                   windowThreshold={20}
//                   optionHeight={35}
//                   maxHeight={200}
//                 />
//               </Form.Group>
//             </Row>
//             <Row>
//               <Col xs={6} md={6}>
//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Category {editingData?.categoryString}</Form.Label>
//                   <Form.Select
//                     name="category"
//                     value={product.category || ""}
//                     onChange={handleChange}
//                     style={{ fontSize: 13 }}
//                     className={editingData?.categoryString ? "" : "is-invalid"}
//                     required={!editingData?.categoryString}
//                   >
//                     <option value="" disabled>-- Select a category --</option>
//                     {categories.map((category) => (
//                       <option style={{ fontSize: 13 }} key={category.id} value={String(category.id)}>
//                         {category.name}
//                       </option>
//                     ))}
//                   </Form.Select>
//                   {!product.category && (
//                     <div className="invalid-feedback">Category is required.</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Age Group Product {editingData?.ageGroupProductString}</Form.Label>
//                   <Form.Select
//                     name="ageGroupProduct"
//                     value={product.ageGroupProduct || ""}
//                     onChange={handleChange}
//                     style={{ fontSize: 13 }}
//                     className={editingData?.ageGroupProductString ? "" : "is-invalid"}
//                     required={!editingData?.ageGroupProductString}
//                   >
//                     <option value="" disabled>-- Select a Age Group --</option>
//                     {ageGroupProduct.map((ageGroup) => (
//                       <option style={{ fontSize: 13 }} key={ageGroup.id} value={String(ageGroup.id)}>
//                         {ageGroup.name}
//                       </option>
//                     ))}
//                   </Form.Select>
//                   {!product.ageGroupProduct && (
//                     <div className="invalid-feedback">Age Group is required.</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Type Product {editingData?.typeProductString}</Form.Label>
//                   <Form.Select
//                     name="typeProduct"
//                     value={product.typeProduct || ""}
//                     onChange={handleChange}
//                     style={{ fontSize: 13 }}
//                     className={editingData?.typeProductString ? "" : "is-invalid"}
//                     required={!editingData?.typeProductString}
//                   >
//                     <option value="" disabled>-- Select a Type --</option>
//                     {typeProduct.map((type) => (
//                       <option style={{ fontSize: 13 }} key={type.id} value={String(type.id)}>
//                         {type.name}
//                       </option>
//                     ))}
//                   </Form.Select>
//                   {!product.typeProduct && (
//                     <div className="invalid-feedback">Type is required.</div>
//                   )}
//                 </Form.Group>

//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Price</Form.Label>
//                   <Form.Control
//                     type="number"
//                     name="starting_price"
//                     value={product.starting_price ?? ""}
//                     onChange={handleChange}
//                     style={{ fontSize: 13 }}
//                     required
//                   />
//                 </Form.Group>

//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Release and Expired</Form.Label>
//                   <InputGroup>
//                     <Form.Control
//                       type="date"
//                       name="release_date"
//                       value={product.release_date || ""}
//                       onChange={handleChange}
//                       placeholder="Release date"
//                       style={{ fontSize: 13 }}
//                     />
//                     <Form.Control
//                       type="date"
//                       name="expired_date"
//                       value={product.expired_date || ""}
//                       onChange={handleChange}
//                       placeholder="Expired date"
//                       style={{ fontSize: 13 }}
//                     />
//                   </InputGroup>
//                 </Form.Group>

//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Sex Category</Form.Label>
//                   <Form.Select
//                     name="sex"
//                     value={product.sex || ""}
//                     onChange={handleChange}
//                     style={{ fontSize: 13 }}
//                     required
//                   >
//                     <option value="" disabled>-- Select a Sex Category --</option>
//                     <option value="Laki-laki">Laki - laki</option>
//                     <option value="Perempuan">Perempuan</option>
//                     <option value="Umum">Umum</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>

//               <Col xs={6} md={6}>
//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Name and Id Paket</Form.Label>
//                   <InputGroup>
//                     <Form.Control
//                       type="text"
//                       name="name"
//                       value={product.name || ""}
//                       onChange={handleChange}
//                       placeholder="Name Product"
//                       required
//                     />
//                     <Form.Control
//                       type="text"
//                       name="id_paket"
//                       value={product.id_paket || ""}
//                       onChange={handleChange}
//                       placeholder="Id Paket"
//                       required
//                     />
//                   </InputGroup>
//                 </Form.Group>

//                 <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                   <Form.Label>Alias Name and Meta Title</Form.Label>
//                   <InputGroup>
//                     <Form.Control
//                       type="text"
//                       name="alias_name"
//                       value={product.alias_name || ""}
//                       onChange={handleChange}
//                       placeholder="Alias Name"
//                       required
//                     />
//                     <Form.Control
//                       type="text"
//                       name="meta_title"
//                       value={product.meta_title || ""}
//                       onChange={handleChange}
//                       placeholder="Meta Title"
//                       required
//                     />
//                   </InputGroup>
//                 </Form.Group>

//                 <Form.Group className="mb-2">
//                   <Row>
//                     <Col>
//                       <Form.Check
//                         type="checkbox"
//                         id="is_featured"
//                         name="is_featured"
//                         label="Featured"
//                         checked={product.is_featured === 1}
//                         onChange={handleChange}
//                       />
//                     </Col>
//                     <Col>
//                       <Form.Check
//                         type="checkbox"
//                         id="is_insurance"
//                         name="is_insurance"
//                         label="Insurance"
//                         checked={product.is_insurance === 1}
//                         onChange={handleChange}
//                       />
//                     </Col>
//                     <Col>
//                       <Form.Check
//                         type="checkbox"
//                         id="is_new_release"
//                         name="is_new_release"
//                         label="New release"
//                         checked={product.is_new_release === 1}
//                         onChange={handleChange}
//                       />
//                     </Col>
//                     <Col>
//                       <Form.Check
//                         type="checkbox"
//                         id="is_same_price"
//                         name="is_same_price"
//                         label="Is same price"
//                         checked={product.is_same_price === 1}
//                         onChange={handleChange}
//                       />
//                     </Col>
//                   </Row>
//                   <Row>
//                     <Col>
//                       <Form.Check
//                         type="checkbox"
//                         id="is_price_hide"
//                         name="is_price_hide"
//                         label="Price hide"
//                         checked={product.is_price_hide === 1}
//                         onChange={handleChange}
//                       />
//                     </Col>
//                     <Col>
//                       <Form.Check
//                         type="checkbox"
//                         id="is_promotion_package"
//                         name="is_promotion_package"
//                         label="Promotion Package"
//                         checked={product.is_promotion_package === 1}
//                         onChange={handleChange}
//                       />
//                     </Col>
//                   </Row>
//                   <Row>
//                     <Col>
//                       {showSelect && (
//                         <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                           <Form.Label>Select Promotion</Form.Label>
//                           <Form.Select
//                             name="promo_code"
//                             value={product.promo_code || ""}
//                             onChange={handleChange}
//                             style={{ fontSize: 13 }}
//                           >
//                             <option value="" disabled>-- Select a Promotion --</option>
//                             {promoProduct.map((promo) => (
//                               <option
//                                 style={{ fontSize: 13 }}
//                                 key={promo.id}
//                                 value={promo.code_promo}
//                                 data-discount={promo.discount}
//                               >
//                                 {promo.name} - Discount {promo.discount} %
//                               </option>
//                             ))}
//                           </Form.Select>
//                         </Form.Group>
//                       )}
//                       {product.promo_price ? (
//                         <Form.Group className="mb-2" style={{ fontSize: 13 }}>
//                           <Form.Label>Promo Price</Form.Label>
//                           <Form.Control
//                             type="number"
//                             name="promo_price"
//                             value={product.promo_price ?? ""}
//                             readOnly
//                             style={{ fontSize: 13 }}
//                           />
//                         </Form.Group>
//                       ) : null}
//                     </Col>
//                   </Row>
//                 </Form.Group>
//               </Col>
//             </Row>

//             {/* Bagian Pemilihan Gambar */}
//             <Row>
//               <Col xs={12} md={12}>
//                 {editingData?.image || newImageUrl ? (
//                   <div className="mt-3">
//                     <h5>Select Image:</h5>
//                     <Row>
//                       {editingData?.image && (
//                         <Col xs={6} md={4} className="mb-2">
//                           <Form.Check
//                             type="radio"
//                             label="Current Image"
//                             name="imageOption"
//                             value="old"
//                             checked={imageChoice === "old"}
//                             onChange={() => handleImageChoiceChange("old")}
//                           />
//                           <img
//                             src={editingData.image}
//                             alt="Current"
//                             style={{ maxWidth: "100%", maxHeight: "150px", border: "1px solid #ccc" }}
//                           />
//                         </Col>
//                       )}
//                       {newImageUrl && (
//                         <Col xs={6} md={4} className="mb-2">
//                           <Form.Check
//                             type="radio"
//                             label="New Image from Package"
//                             name="imageOption"
//                             value="new"
//                             checked={imageChoice === "new"}
//                             onChange={() => handleImageChoiceChange("new")}
//                           />
//                           <img
//                             src={newImageUrl}
//                             alt="New"
//                             style={{ maxWidth: "100%", maxHeight: "150px", border: "1px solid #ccc" }}
//                           />
//                         </Col>
//                       )}
//                     </Row>
//                     <div className="mt-2">
//                       <strong>Selected Image:</strong>
//                       {product.image && (
//                         <img
//                           src={product.image}
//                           alt="Selected"
//                           style={{ maxWidth: "200px", maxHeight: "100px", display: "block", marginTop: "5px" }}
//                         />
//                       )}
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="mt-3 text-muted">No image available</div>
//                 )}
//               </Col>
//             </Row>

//             <Button style={{ fontSize: 13 }} variant="primary" type="submit">
//               {editingData?.id ? "Update Product" : "Add Product"}
//             </Button>
//           </Container>
//         </Form>
//       </Modal.Body>
//     </Modal>
//   );
// };

// export default ProductModalAdd;